import type { IntlShape } from "react-intl";
import { getConfig } from "./config";
import { useAgentStore } from "@/stores/agent-store";
import { postTourHintEvent } from "./agent-sessions";

// The "what's new" catalog: one entry per semantically versioned presentation, newest last.
// The CLIENT owns this list; the server only stores the highest version a user has seen or
// dismissed (agent_tour_hint.news_version). A presentation is deliberately lightweight: one
// chat bubble + navigate + pulse the anchored element with the floating hint card — the same
// pieces a clicked tour snippet uses, no tour driver involved.

export interface WhatsNewEntry {
  version: string;
  route: string;
  anchor: string;
  message: (intl: IntlShape) => string;
  /** Availability gate — a disabled entry is skipped ENTIRELY (not pending, no nag). */
  enabled?: () => boolean;
}

export const WHATS_NEW: readonly WhatsNewEntry[] = [
  {
    version: "0.1",
    route: "/settings?section=claude",
    anchor: "settings.claude",
    message: (intl) =>
      intl.formatMessage({
        id: "whatsNew.v0_1",
        defaultMessage:
          "New in Smycles: connect the Claude apps to your data. This settings page shows you how, with the MCP endpoint, the client id and a step-by-step guide.",
      }),
    enabled: () => getConfig().mcp.enabled,
  },
];

function parts(v: string): number[] {
  return v.split(".").map((p) => Number.parseInt(p, 10) || 0);
}

/** Numeric part-wise semver-ish compare: "0.1" < "0.2" < "0.10". */
function newerThan(a: string, b: string | null): boolean {
  if (b == null) return true;
  const pa = parts(a);
  const pb = parts(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da > db;
  }
  return false;
}

/** Every availability-gate-passing entry, oldest first. */
export function availableEntries(): WhatsNewEntry[] {
  return WHATS_NEW.filter((e) => e.enabled?.() ?? true);
}

/** Available entries newer than [stored], oldest first (a state-a user gets 0.1 AND 0.2). */
export function pendingVersions(stored: string | null | undefined): WhatsNewEntry[] {
  return availableEntries().filter((e) => newerThan(e.version, stored ?? null));
}

/** The newest available version, or null when the catalog is empty/gated off. */
export function latestVersion(): string | null {
  const available = availableEntries();
  return available.length ? available[available.length - 1]!.version : null;
}

const BETWEEN_VERSIONS_MS = 8000;

/**
 * Present the pending entries oldest-first: chat bubble, navigate, pulse the element with the
 * floating card. Marks the latest version seen afterwards (fire-and-forget).
 */
export async function runWhatsNew(
  pending: readonly WhatsNewEntry[],
  deps: { navigate: (path: string) => void; intl: IntlShape },
): Promise<void> {
  if (!pending.length) return;
  // Open the drawer ON the chat view so the news bubble is actually visible (the drawer may
  // have been closed or sitting on the Chats list).
  useAgentStore.setState({ open: true, view: "chat" });
  for (const [i, entry] of pending.entries()) {
    const message = entry.message(deps.intl);
    useAgentStore.getState().appendMessage("assistant", message);
    deps.navigate(entry.route);
    useAgentStore.getState().setHighlightHint({ text: message });
    useAgentStore.getState().setHighlightAnchor(entry.anchor);
    if (i < pending.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, BETWEEN_VERSIONS_MS));
    }
  }
  const latest = latestVersion();
  if (latest) void postTourHintEvent("news-seen", latest).catch(() => undefined);
}
